'use client';

import { ChangeEvent, FormEvent, RefObject, useState } from 'react';
import { type DownloadButtonProps } from '@/ui-components/molecules/AppDownloadButtons/AppDownloadButtons';
import { SnapshotPopoverShell } from './SnapshotPopoverShell';
import { SnapshotUserAnonymousSignIn } from './SnapshotUserAnonymousSignIn';
import { SnapshotUserChooseSignInMethod } from './SnapshotUserChooseSignInMethod';
import { SignInMethod, SnapshotUserErrorType, SnapshotUserStep } from './SnapshotUser.types';
// UI-only mock for local and Storybook QA.
// Replace this with the real identity lookup when the sign-in API/SDK is wired.
const NOT_FOUND_MOCK_IDENTIFIERS = new Set(['missing@example.com', 'missing']);

const FALLBACK_APP_DOWNLOAD_ITEMS: DownloadButtonProps[] = [
    {
        storeLink: {
            href: 'https://apps.apple.com/us/app/siriusxm-music-sports-news/id317951436',
            target: '_blank',
        },
        badgeSrc: 'https://www.siriusxm.com/content/dam/sxm-com/logos/banners/Download_on_the_App_Store_Badge.svg',
        type: 'appleStore',
    },
    {
        storeLink: {
            href: 'https://play.google.com/store/apps/details?id=com.sirius&hl=en&gl=US',
            target: '_blank',
        },
        badgeSrc: 'https://www.siriusxm.com/content/dam/sxm-com/logos/banners/Google_Play_Store_badge_EN.svg',
        type: 'playStore',
    },
];

interface SnapshotUserPopoverProps {
    appdownloadItem?: DownloadButtonProps[];
    buttonRef: RefObject<HTMLButtonElement | null>;
    open: boolean;
    onClose: () => void;
    initialIdentifier?: string;
    initialErrorType?: SnapshotUserErrorType;
    initialStep?: SnapshotUserStep;
}

export function SnapshotUserPopover({
    appdownloadItem,
    buttonRef,
    open,
    onClose,
    initialIdentifier = '',
    initialErrorType = 'none',
    initialStep = 'enterIdentifier',
}: SnapshotUserPopoverProps): React.JSX.Element | null {
    const [identifier, setIdentifier] = useState(initialIdentifier);
    const [errorType, setErrorType] = useState<SnapshotUserErrorType>(initialErrorType);
    const [step, setStep] = useState<SnapshotUserStep>(initialStep);
    const [selectedMethod, setSelectedMethod] = useState<SignInMethod>('text');
    const resolvedAppDownloadItems = appdownloadItem ?? FALLBACK_APP_DOWNLOAD_ITEMS;

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        // empty input
        if (!identifier.trim()) {
            setErrorType('blank');
            return;
        }

        if (NOT_FOUND_MOCK_IDENTIFIERS.has(identifier.trim().toLowerCase())) {
            setErrorType('notFound');
            return;
        }

        setErrorType('none');
        setStep('chooseSignInMethod');
    };

    const handleIdentifierChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value;
        setIdentifier(nextValue);

        if (errorType !== 'none') {
            setErrorType('none');
        }
    };

    let invalidMessage: string | undefined;

    if (errorType === 'blank') {
        invalidMessage = 'Enter your email or username.';
    } else if (errorType === 'notFound') {
        invalidMessage = "We couldn't find an account with that email or username.";
    }

    return (
        <SnapshotPopoverShell ariaLabel="Sign in" buttonRef={buttonRef} onClose={onClose} open={open}>
            {step === 'chooseSignInMethod' ? (
                <SnapshotUserChooseSignInMethod
                    appdownloadItems={resolvedAppDownloadItems}
                    onBack={() => setStep('enterIdentifier')}
                    onSelectMethod={setSelectedMethod}
                    selectedMethod={selectedMethod}
                />
            ) : (
                <SnapshotUserAnonymousSignIn
                    appdownloadItems={resolvedAppDownloadItems}
                    hasError={errorType !== 'none'}
                    identifier={identifier}
                    invalidMessage={invalidMessage}
                    onIdentifierChange={handleIdentifierChange}
                    onSubmit={handleSubmit}
                />
            )}
        </SnapshotPopoverShell>
    );
}
